import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Input, Button, Card, Result, Row, Col } from 'antd';
import { MailOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useForgotPasswordMutation } from '../../store/slices/authSlice';
import { VersionInfo } from '../../utils/version';
import CaptchaInput from '../../components/auth/CaptchaInput';

interface ForgotPasswordFormData {
  email: string;
  captchaCode: string;
}

const ForgotPasswordPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [captchaId, setCaptchaId] = useState<string>('');
  const [captchaRefreshTrigger, setCaptchaRefreshTrigger] = useState(0);
  const resolveLocalizedMessage = (
    backendMessage: string | undefined,
    fallback: string
  ) => {
    if (!backendMessage) return fallback;
    const isZh = i18n.language.toLowerCase().startsWith('zh');
    const hasChinese = /[\u4e00-\u9fff]/.test(backendMessage);
    return !isZh && hasChinese ? fallback : backendMessage;
  };

  const handleSubmit = async (values: ForgotPasswordFormData) => {
    setErrorMessage('');

    // 验证验证码
    if (!captchaId || !values.captchaCode) {
      setErrorMessage(t('auth:login.captcha.required'));
      return;
    }

    try {
      const captchaResponse = await fetch('/api/v1/auth/captcha/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          captchaId,
          captchaCode: values.captchaCode,
        }),
      });
      const captchaResult = await captchaResponse.json();

      if (!captchaResult.success) {
        setErrorMessage(
          resolveLocalizedMessage(
            captchaResult.error?.message,
            t('auth:login.captcha.invalid')
          )
        );
        form.setFieldsValue({ captchaCode: '' });
        setCaptchaRefreshTrigger(prev => prev + 1);
        return;
      }
    } catch {
      setErrorMessage(t('auth:login.captcha.invalid'));
      setCaptchaRefreshTrigger(prev => prev + 1);
      return;
    }

    try {
      const result = await forgotPassword({ email: values.email }).unwrap();

      if (result.success) {
        setSubmittedEmail(values.email);
        setSubmitted(true);
      } else {
        setErrorMessage(
          resolveLocalizedMessage(result.message, t('auth:forgotPassword.failed'))
        );
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      const errorMsg =
        error?.data?.error?.message ||
        error?.data?.message ||
        t('auth:forgotPassword.failed');
      setErrorMessage(
        resolveLocalizedMessage(errorMsg, t('auth:forgotPassword.failed'))
      );
    }
  };

  const handleResend = async () => {
    if (!submittedEmail) return;

    setErrorMessage('');

    try {
      const result = await forgotPassword({ email: submittedEmail }).unwrap();

      if (!result.success) {
        setErrorMessage(
          resolveLocalizedMessage(result.message, t('auth:forgotPassword.failed'))
        );
      }
    } catch (error: any) {
      console.error('Resend password error:', error);
      const errorMsg =
        error?.data?.error?.message ||
        error?.data?.message ||
        t('auth:forgotPassword.failed');
      setErrorMessage(
        resolveLocalizedMessage(errorMsg, t('auth:forgotPassword.failed'))
      );
    }
  };

  return (
    <Row className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* 左侧品牌展示区域 */}
      <Col
        xs={0}
        sm={0}
        md={0}
        lg={14}
        xl={16}
        style={{
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-600"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-white/5"></div>

        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-white/15 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 opacity-5">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
        </div>

        <div
          className="relative z-10 text-white max-w-2xl"
          style={{ padding: '0 4rem', width: '100%' }}
        >
          <div className="mb-16">
            <div className="flex items-center mb-8">
              <div className="w-16 h-16 bg-white/25 backdrop-blur-sm rounded-2xl flex items-center justify-center mr-5 border border-white/40 shadow-lg">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <span className="text-2xl">🌱</span>
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight drop-shadow-sm">
                  {t('auth:brand.name')}
                </h1>
                <p className="text-white/85 text-base font-light">
                  Offshore Green Bond Express
                </p>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <div className="space-y-4">
              <h2 className="text-3xl xl:text-4xl font-bold leading-tight tracking-tight drop-shadow-sm">
                {t('auth:forgotPassword.leftTitlePrefix')}
                <br />
                <span className="bg-gradient-to-r from-white via-emerald-100 to-emerald-200 bg-clip-text text-transparent">
                  {t('auth:forgotPassword.leftTitleHighlight')}
                </span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-white via-white/80 to-white/50 rounded-full shadow-sm"></div>
            </div>
            <p className="text-lg text-white/90 leading-relaxed mt-8 max-w-lg">
              {t('auth:forgotPassword.leftDescription')}
            </p>
          </div>
        </div>
      </Col>

      {/* 右侧表单区域 */}
      <Col
        xs={24}
        sm={24}
        md={24}
        lg={10}
        xl={8}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.5rem',
          minHeight: '100vh',
        }}
      >
        <div className="w-full max-w-md">
          <Card
            className="shadow-xl border-0 overflow-hidden"
            style={{ borderRadius: '16px' }}
            styles={{ body: { padding: 0 } }}
          >
            {/* 卡片头部 */}
            <div
              className="px-8 pt-6 pb-4"
              style={{
                background:
                  'linear-gradient(180deg, rgba(16, 185, 129, 0.08) 0%, rgba(255,255,255,0) 100%)',
              }}
            >
              <h2 className="text-xl font-bold text-gray-800 mb-1">
                {t('auth:forgotPassword.title')}
              </h2>
              <p className="text-gray-500 text-sm">
                {t('auth:forgotPassword.subtitle')}
              </p>
            </div>

            {/* 表单区域 */}
            <div className="px-8 py-6 bg-gradient-to-b from-white to-gray-50/30">
              {submitted ? (
                <Result
                  status="success"
                  title={t('auth:forgotPassword.success')}
                  subTitle={
                    <div className="text-gray-600">
                      <p>{t('auth:forgotPassword.successMessage')}</p>
                      {errorMessage && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-left">
                          <p className="text-red-600 text-sm">{errorMessage}</p>
                        </div>
                      )}
                    </div>
                  }
                  extra={[
                    <Link to="/auth/login" key="login">
                      <Button
                        type="primary"
                        size="large"
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {t('auth:forgotPassword.backToLogin')}
                      </Button>
                    </Link>,
                    <Button
                      key="retry"
                      size="large"
                      loading={isLoading}
                      onClick={handleResend}
                    >
                      {t('auth:activate.resend')}
                    </Button>,
                  ]}
                />
              ) : (
                <>
                  {errorMessage && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-600 text-sm">{errorMessage}</p>
                    </div>
                  )}

                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    size="large"
                  >
                    <Form.Item
                      name="email"
                      label={
                        <span className="text-gray-700 font-medium">
                          {t('auth:forgotPassword.email.label')}
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: t('auth:forgotPassword.email.required'),
                        },
                        {
                          type: 'email',
                          message: t('auth:forgotPassword.email.invalid'),
                        },
                      ]}
                    >
                      <Input
                        prefix={<MailOutlined className="text-gray-400" />}
                        placeholder={t('auth:forgotPassword.email.placeholder')}
                        className="rounded-lg"
                      />
                    </Form.Item>

                    <Form.Item
                      name="captchaCode"
                      label={
                        <span className="text-gray-700 font-medium">
                          {t('auth:login.captcha.label')}
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: t('auth:login.captcha.required'),
                        },
                      ]}
                    >
                      <CaptchaInput
                        onCaptchaIdChange={setCaptchaId}
                        placeholder={t('auth:login.captcha.placeholder')}
                        refreshTrigger={captchaRefreshTrigger}
                      />
                    </Form.Item>

                    <Form.Item className="mb-4">
                      <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={isLoading}
                        className="h-11 rounded-lg font-medium bg-emerald-600 hover:bg-emerald-700 border-0"
                      >
                        {isLoading
                          ? t('auth:forgotPassword.submitting')
                          : t('auth:forgotPassword.submit')}
                      </Button>
                    </Form.Item>

                    <div className="text-center">
                      <Link
                        to="/auth/login"
                        className="text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1"
                      >
                        <ArrowLeftOutlined />
                        {t('auth:forgotPassword.backToLogin')}
                      </Link>
                    </div>
                  </Form>
                </>
              )}
            </div>
          </Card>

          {/* 版权信息 */}
          <div className="text-center mt-6">
            <p className="text-gray-400 text-sm">
              {t('common:app.copyright', { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default ForgotPasswordPage;
